import { useMemo } from 'react';

import type { BadgeType } from '@scaleaq/scaleui';

import {
  WinchAccuracy,
  WinchCalibrationState,
  WinchCalibrationType,
  type WinchCalibrationInfo,
} from '@scaleaq/sitecontrol-lib';
import { useTranslation } from 'react-i18next';

export type WinchCalibrationBadge = {
  key: string;
  type: BadgeType;
  label: string;
};

/**
 * Badges for the calibration status of a winch. The badges are mutually exclusive in this order:
 * uncalibrated, then running, then the failed/aborted state combined with bad accuracy.
 */
export const useWinchCalibrationBadges = (
  calibration: WinchCalibrationInfo | undefined
): WinchCalibrationBadge[] => {
  const { t } = useTranslation('cameraControl');

  return useMemo(() => {
    if (calibration?.type === WinchCalibrationType.Uncalibrated) {
      return [{ key: 'uncalibrated', type: 'warning', label: t('winchUncalibrated') }];
    }
    if (calibration?.state === WinchCalibrationState.Running) {
      return [{ key: 'state', type: 'critical', label: t('winchCalibrationRunning') }];
    }

    const badges: WinchCalibrationBadge[] = [];
    if (calibration?.state === WinchCalibrationState.Aborted) {
      badges.push({ key: 'state', type: 'warning', label: t('winchCalibrationAborted') });
    }
    if (calibration?.state === WinchCalibrationState.Failed) {
      badges.push({ key: 'state', type: 'warning', label: t('winchCalibrationFailed') });
    }
    if (calibration?.accuracy === WinchAccuracy.Bad) {
      const accuracyLabels = {
        [WinchAccuracy.Bad]: t('winchAccuracyBad'),
        [WinchAccuracy.Fair]: t('winchAccuracyFair'),
        [WinchAccuracy.Good]: t('winchAccuracyGood'),
      };
      badges.push({
        key: 'accuracy',
        type: 'warning',
        label: t('winchAccuracy', { accuracy: accuracyLabels[calibration.accuracy] }),
      });
    }
    return badges;
  }, [calibration, t]);
};
